import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { adminRequestsService } from '../services/admin.requests.service'
import type { ServiceRequestDocument, RequestStatus } from '@/types'

const STATUS_BADGE: Record<
  RequestStatus,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }
> = {
  pending:   { variant: 'secondary',   label: 'Pending'   },
  reviewed:  { variant: 'default',     label: 'Reviewed'  },
  declined:  { variant: 'destructive', label: 'Declined'  },
  converted: { variant: 'outline',     label: 'Converted' },
}

const DISCOUNT_LABELS: Record<string, string> = {
  none:       'None',
  student:    'Student',
  nonprofit:  '501(c)(3) Nonprofit',
  odd_artist: 'ODD Artist',
}

function formatDate(val: unknown): string {
  if (!val) return '—'
  if (val instanceof Date) return val.toLocaleString()
  if (typeof val === 'object' && val !== null && 'toDate' in val) {
    return (val as { toDate: () => Date }).toDate().toLocaleString()
  }
  return '—'
}

interface AdminRequestDetailModalProps {
  requestId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onStatusChange?: (id: string, status: RequestStatus) => void
  onConvertToEngagement?: (request: ServiceRequestDocument & { id: string }) => void
  /** For surfacing member names next to availableProviderMemberIds. */
  members?: Array<{ id: string; name: string }>
}

export function AdminRequestDetailModal({
  requestId,
  open,
  onOpenChange,
  onStatusChange,
  onConvertToEngagement,
  members = [],
}: AdminRequestDetailModalProps) {
  const [request, setRequest] = useState<(ServiceRequestDocument & { id: string }) | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open || !requestId) return
    setLoading(true)
    setLoadError(null)
    setRequest(null)
    adminRequestsService
      .getRequestById(requestId)
      .then(setRequest)
      .catch((err: unknown) =>
        setLoadError(formatUserFacingError(err, 'Failed to load request'))
      )
      .finally(() => setLoading(false))
  }, [requestId, open])

  async function handleStatusChange(status: RequestStatus) {
    if (!request) return
    setSaving(true)
    try {
      await adminRequestsService.updateStatus(request.id, status)
      setRequest((prev) => (prev ? { ...prev, status } : prev))
      onStatusChange?.(request.id, status)
    } finally {
      setSaving(false)
    }
  }

  const title = loading
    ? 'Loading…'
    : loadError
      ? 'Error'
      : request
        ? `${request.firstName} ${request.lastName}`
        : 'Service Request'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl flex flex-col max-h-[85vh] gap-0 p-0">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 px-6 py-5 [scrollbar-width:thin] [scrollbar-color:hsl(var(--muted-foreground)/0.35)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/50">
          {loading && (
            <div className="space-y-3">
              <Skeleton className="h-8 w-36" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          )}

          {!loading && loadError && (
            <p className="text-sm text-destructive">{loadError}</p>
          )}

          {!loading && !loadError && request && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">Status</span>
                <Badge variant={STATUS_BADGE[request.status].variant}>
                  {STATUS_BADGE[request.status].label}
                </Badge>
              </div>

              <Separator />

              {/* Contact */}
              <section className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Contact
                </h3>
                <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Name</dt>
                  <dd>{request.firstName} {request.lastName}</dd>
                  <dt className="text-muted-foreground">Email</dt>
                  <dd>{request.email}</dd>
                  {request.organizationName && (
                    <>
                      <dt className="text-muted-foreground">Organization</dt>
                      <dd>{request.organizationName}</dd>
                    </>
                  )}
                  <dt className="text-muted-foreground">Discount</dt>
                  <dd>
                    {DISCOUNT_LABELS[request.discountCategory] ?? request.discountCategory}
                    {request.discountAppliedPercent > 0 && (
                      <span className="ml-1 text-muted-foreground">
                        ({request.discountAppliedPercent}% off)
                      </span>
                    )}
                  </dd>
                  <dt className="text-muted-foreground">Mailing list</dt>
                  <dd>{request.mailingOptIn ? 'Yes' : 'No'}</dd>
                  <dt className="text-muted-foreground">Submitted</dt>
                  <dd>{formatDate(request.createdAt)}</dd>
                </dl>
              </section>

              <Separator />

              {/* Services */}
              <section className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Services
                </h3>
                <div className="space-y-3">
                  {request.services.map((svc, idx) => (
                    <div key={idx} className="rounded-lg border p-4 space-y-2 text-sm">
                      <p className="font-medium">{svc.subType}</p>
                      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-muted-foreground">
                        {svc.date && (
                          <>
                            <dt>Date / Time</dt>
                            <dd className="text-foreground">{svc.date}{svc.time ? ` at ${svc.time}` : ''}</dd>
                          </>
                        )}
                        {svc.location && (
                          <>
                            <dt>Location</dt>
                            <dd className="text-foreground">{svc.location}</dd>
                          </>
                        )}
                        {svc.hours !== undefined && (
                          <>
                            <dt>Hours</dt>
                            <dd className="text-foreground">{svc.hours}</dd>
                          </>
                        )}
                        {svc.filmLengthMinutes !== undefined && (
                          <>
                            <dt>Film length</dt>
                            <dd className="text-foreground">{svc.filmLengthMinutes} min</dd>
                          </>
                        )}
                        {svc.headshots !== undefined && (
                          <>
                            <dt>Headshots</dt>
                            <dd className="text-foreground">{svc.headshots}</dd>
                          </>
                        )}
                        {svc.complexity && (
                          <>
                            <dt>Complexity</dt>
                            <dd className="text-foreground capitalize">{svc.complexity}</dd>
                          </>
                        )}
                        {svc.includeAudioRecording !== undefined && (
                          <>
                            <dt>Audio recording</dt>
                            <dd className="text-foreground">{svc.includeAudioRecording ? 'Yes' : 'No'}</dd>
                          </>
                        )}
                      </dl>
                      {svc.addons.length > 0 && (
                        <div className="pt-1 border-t mt-2 space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">Add-ons</p>
                          {svc.addons.map((a) => (
                            <p key={a.addonId} className="text-xs">
                              {a.label}: <span className="font-medium">{String(a.value)}</span>
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              <Separator />

              {/* Available Providers */}
              <section className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Available Providers
                </h3>
                {(() => {
                  const availIds = request.availableProviderMemberIds ?? []
                  const notifiedIds = request.notifiedProviderMemberIds ?? []
                  if (notifiedIds.length === 0) {
                    return (
                      <p className="text-sm text-muted-foreground">
                        No providers were notified for this request. (No services with provider positions matched.)
                      </p>
                    )
                  }
                  if (availIds.length === 0) {
                    return (
                      <p className="text-sm text-muted-foreground">
                        Notified {notifiedIds.length} provider{notifiedIds.length === 1 ? '' : 's'}; none have marked themselves Available yet.
                      </p>
                    )
                  }
                  const namesById = new Map(members.map((m) => [m.id, m.name]))
                  return (
                    <>
                      <p className="text-xs text-muted-foreground">
                        {availIds.length} of {notifiedIds.length} notified provider{notifiedIds.length === 1 ? '' : 's'} marked Available:
                      </p>
                      <ul className="flex flex-wrap gap-2">
                        {availIds.map((id) => (
                          <li key={id}>
                            <Badge variant="default">
                              {namesById.get(id) ?? `Member ${id.slice(0, 6)}…`}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                    </>
                  )
                })()}
              </section>

              <Separator />

              {/* Quote & notes */}
              <section className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Quote & Notes
                </h3>
                <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Estimated total</dt>
                  <dd className="font-mono font-semibold">{request.quoteTotal}</dd>
                  {request.additionalInfo && (
                    <>
                      <dt className="text-muted-foreground">Additional info</dt>
                      <dd>{request.additionalInfo}</dd>
                    </>
                  )}
                  {request.requestedProviders && (
                    <>
                      <dt className="text-muted-foreground">Requested providers</dt>
                      <dd>{request.requestedProviders}</dd>
                    </>
                  )}
                </dl>
              </section>
            </div>
          )}
        </div>

        {request && (
          <DialogFooter className="border-t px-6 py-3">
            {request.status === 'converted' && (
              <p className="mr-auto text-xs text-muted-foreground">
                Converted to engagement — no further action available here.
              </p>
            )}
            {request.status !== 'declined' && request.status !== 'converted' && (
              <Button
                variant="destructive"
                onClick={async () => {
                  if (confirm(`Decline request from ${request.firstName} ${request.lastName}?`)) {
                    await handleStatusChange('declined')
                  }
                }}
                disabled={saving}
              >
                Decline
              </Button>
            )}
            {onConvertToEngagement &&
              request.status !== 'declined' &&
              request.status !== 'converted' && (
                <Button onClick={() => onConvertToEngagement(request)}>
                  Convert to Engagement
                </Button>
              )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
